import { DropdownContext } from 'components/DropdownContext';
import type { ItemAction } from 'components/DropdownContext';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useDeleteBimOrderChange } from 'pages/Project/BimProject/hooks/useDeleteBimOrderChange';
import { ExportWorkOrderChangeBim } from 'pages/Project/BimProject/WorkOrderChange/ExportWorkOrderChangeBim/ExportWorkOrderChangeBim';
import { ExportWorkOrderChangeAnalytics } from 'pages/Project/Common/ExportWorkOrderChangeAnalytics/ExportWorkOrderChangeAnalytics';
import { Tooltip } from 'uikit';

import { useCallback, useMemo, useState } from 'react';

interface IBimWorkOrderChangeKebabMenu {
    projectStatus: ProjectStatusEnum | undefined;
    contractStatus: ContractStatusEnum | undefined;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
    workOrderId: number;
    label: string;
    workOrderChangeId: number;
}

export const BimWorkOrderChangeKebabMenu = ({
    contractStatus,
    projectStatus,
    workOrderChangeStatus,
    workOrderChangeId,
    workOrderId,
    label,
}: IBimWorkOrderChangeKebabMenu) => {
    const [isExportWorkOrderChangeAnalytics, setIsExportWorkOrderChangeAnalytics] = useState<boolean>(false);
    const [isExportWorkOrderChangeBim, setIsExportWorkOrderChangeBim] = useState<boolean>(false);

    const {
        orderChangeDeleteBtnTitle,
        deleteBimOrderChangeHandler,
        DeleteOrderChangeErrorHandler,
        DeleteOrderChangeConfirmDialog,
        orderChangeDeleteTooltipText,
    } = useDeleteBimOrderChange({
        workOrderChangeId,
        workOrderId,
    });

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    const isProjectStatusDelayed = projectStatus === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = projectStatus === ProjectStatusEnum.Completed;
    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = contractStatus === ContractStatusEnum.InImplementation;

    const isWorkOrderChangeStatusOnFormation = workOrderChangeStatus === WorkOrderChangeStatusEnum.OnFormation;

    const isItemWorkOrderChangeDeleteDisabled =
        !isContractStatusWork || isDisabledByProjectStatus || !isWorkOrderChangeStatusOnFormation;

    const menuItems = useMemo<ItemAction[]>(
        () => [
            {
                label: 'Выгрузить аналитику изменения НЗ',
                action: () => setIsExportWorkOrderChangeAnalytics(true),
            },
            {
                label: 'Выгрузить Изм. к НЗ',
                action: () => setIsExportWorkOrderChangeBim(true),
            },
            {
                label: orderChangeDeleteBtnTitle,
                action: deleteBimOrderChangeHandler,
                disabled: isItemWorkOrderChangeDeleteDisabled,
                haveTooltip: !isWorkOrderChangeStatusOnFormation,
                tooltipName: 'cantRemoveOrderChangeByOrderChangeStatus',
                tooltipMsg: orderChangeDeleteTooltipText,
            },
        ],
        [
            orderChangeDeleteBtnTitle,
            deleteBimOrderChangeHandler,
            isItemWorkOrderChangeDeleteDisabled,
            isWorkOrderChangeStatusOnFormation,
            orderChangeDeleteTooltipText,
        ],
    );

    const onCloseExportWorkOrderChangeAnalyticsClick = useCallback(() => {
        setIsExportWorkOrderChangeAnalytics(false);
    }, []);

    const onCloseExportWorkOrderChangeBimClick = useCallback(() => {
        setIsExportWorkOrderChangeBim(false);
    }, []);

    return (
        <>
            <DropdownContext
                items={menuItems}
                btnView="ghost"
                btnSize="s"
                btnIconSize="s"
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseLeaveHandler={onMenuMouseLeave}
                onMouseMoveHandler={onMenuMouseMove}
            />
            {isExportWorkOrderChangeAnalytics && (
                <ExportWorkOrderChangeAnalytics
                    workOrderChangeId={workOrderChangeId}
                    onClose={onCloseExportWorkOrderChangeAnalyticsClick}
                    label={label}
                />
            )}
            {isExportWorkOrderChangeBim && (
                <ExportWorkOrderChangeBim
                    workOrderChangeId={workOrderChangeId}
                    onClose={onCloseExportWorkOrderChangeBimClick}
                />
            )}
            {DeleteOrderChangeErrorHandler}
            {DeleteOrderChangeConfirmDialog}
            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="upCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
        </>
    );
};

BimWorkOrderChangeKebabMenu.displayName = 'BimWorkOrderChangeKebabMenu';
